import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CLASS_STYLES, STATUSES, CLASSIFICATIONS, ScoreRing, scoreColor } from "@/lib/match";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { ArrowLeft, AlertTriangle, CheckCircle2, ShieldAlert, Repeat, MessageSquareText, Loader2, ArrowRight, Sparkles, ShieldCheck, FileText, Share2, Mail, Printer, ChevronDown, RefreshCw, ArrowRightLeft } from "lucide-react";

const listify = (a) => (Array.isArray(a) ? a : []);

const FIT_DIMS = [["technical", "Technical Fit"], ["cultural", "Cultural Fit"], ["leadership", "Leadership Potential"], ["growth", "Growth Potential"], ["reliability", "Reliability Prediction"], ["communication", "Communication Strength"]];
const fitColor = (v) => v === "High" ? "text-emerald-700 bg-emerald-50 border-emerald-200" : v === "Medium" ? "text-amber-700 bg-amber-50 border-amber-200" : v === "Low" ? "text-red-700 bg-red-50 border-red-200" : "text-slate-500 bg-slate-50 border-slate-200";
const confBadge = (l) => l === "High" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : l === "Medium" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200";

const INTERVIEW_CATS = [["behavioral", "Behavioral"], ["clinical", "Clinical"], ["licensure", "Licensure Verification"], ["scenario", "Scenario-Based"], ["culture", "Culture"], ["leadership", "Leadership"]];
const ACTIONS = [
  { label: "Move to Screening", status: "screening_scheduled" },
  { label: "Submit to Client", status: "submitted_to_client" },
  { label: "Request Documents", status: "contact_candidate" },
  { label: "Hold", status: "hold" },
  { label: "Not Qualified", status: "not_selected" },
  { label: "Save for Future", status: "hold", toast: "Saved for future consideration" },
];

const Field = ({ label, value }) => (
  <div><div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div><div className="text-sm mt-0.5">{value || <span className="text-muted-foreground italic">not found on résumé</span>}</div></div>
);

export default function CandidateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [selectedPid, setSelectedPid] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [aLoading, setALoading] = useState(false);
  const [aError, setAError] = useState("");
  const [ovOpen, setOvOpen] = useState(false);
  const [ovClass, setOvClass] = useState("");
  const [ovNote, setOvNote] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignName, setAssignName] = useState("");
  const [reparsing, setReparsing] = useState(false);
  const interviewRef = useRef(null);
  const analysisReqRef = useRef(0);
  const pollRef = useRef(null);

  const load = useCallback(() => api.get(`/candidates/${id}`).then((r) => {
    setC(r.data);
    setSelectedPid((prev) => prev || (r.data.matches?.[0]?.position_id ?? null));
  }).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  const fetchAnalysis = useCallback(async (pid, refresh = false) => {
    if (!pid) return;
    const reqId = ++analysisReqRef.current;
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    setALoading(true); setAError("");
    const poll = async (isRefresh) => {
      try {
        const { data } = await api.get(`/candidates/${id}/analysis/${pid}${isRefresh ? "?refresh=1" : ""}`);
        if (reqId !== analysisReqRef.current) return;
        if (data.status === "generating") {
          pollRef.current = setTimeout(() => poll(false), 3000);
          return;
        }
        if (data.status === "error") { setAnalysis(null); setAError(data.detail || "Analysis unavailable"); setALoading(false); return; }
        setAnalysis(data); setALoading(false);
      } catch (e) {
        if (reqId !== analysisReqRef.current) return;
        setAnalysis(null);
        setAError(e.response?.data?.detail || "Analysis unavailable");
        setALoading(false);
      }
    };
    poll(refresh);
  }, [id]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  useEffect(() => { if (selectedPid) fetchAnalysis(selectedPid); }, [selectedPid, fetchAnalysis]);

  const changeStatus = async (status, msg) => { await api.patch(`/candidates/${id}/status`, { status }); toast.success(msg || "Status updated"); load(); };
  const submitOverride = async () => {
    await api.patch(`/candidates/${id}/override`, { position_id: selectedPid, revised_classification: ovClass, note: ovNote });
    toast.success("Override saved for quality improvement"); setOvOpen(false); setOvNote(""); load(); fetchAnalysis(selectedPid);
  };
  const submitAssign = async () => { await api.patch(`/candidates/${id}/assign`, { status: assignName }); toast.success(`Assigned to ${assignName}`); setAssignOpen(false); load(); };

  const reparse = async () => {
    try {
      await api.post(`/candidates/${id}/reparse`);
      setReparsing(true);
      toast.success("Re-parsing with premium AI…");
      const poll = async () => {
        try {
          const { data } = await api.get(`/candidates/${id}`);
          if (data.reparse_status === "processing") { setTimeout(poll, 3000); return; }
          setReparsing(false);
          setC(data);
          if (data.reparse_status === "error") toast.error(data.reparse_error || "Re-parse failed");
          else { toast.success("Candidate re-parsed with premium AI"); if (selectedPid) fetchAnalysis(selectedPid, true); }
        } catch { setReparsing(false); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReparsing(false);
      toast.error(e.response?.data?.detail || "Re-parse failed");
    }
  };

  if (!c) return <div className="p-8">Loading…</div>;
  const top = c.matches || [];

  return (
    <div className="p-8 max-w-[1400px]">
      <button onClick={() => navigate("/candidates")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"><ArrowLeft size={15} /> Back to candidates</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 fade-up">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head text-3xl font-semibold tracking-tight">{c.name || "Unnamed candidate"}</h1>
            {c.duplicate_flag && <Badge variant="outline" className="bg-[hsl(var(--gold))]/15 text-[hsl(38,70%,32%)] border-[hsl(var(--gold))]/40"><AlertTriangle size={13} className="mr-1" />Possible duplicate</Badge>}
          </div>
          <p className="text-muted-foreground mt-1">{c.current_title} {c.city && `· ${c.city}, ${c.state}`}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge className="bg-[hsl(var(--teal))]">{c.primary_classification || "Classification pending"}</Badge>
            {listify(c.secondary_classifications).map((s) => <Badge key={s} variant="secondary">{s}</Badge>)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Recruiter status</div>
          <Select value={c.status} onValueChange={(v) => changeStatus(v)}>
            <SelectTrigger className="w-[200px]" data-testid="status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
          {c.assigned_recruiter && <div className="text-xs text-muted-foreground mt-2">Recruiter: {c.assigned_recruiter}</div>}
          <Button variant="outline" size="sm" onClick={reparse} disabled={reparsing} data-testid="reparse-btn" className="mt-3 w-full">
            {reparsing ? <><Loader2 size={14} className="mr-1.5 animate-spin" />Re-parsing…</> : <><Sparkles size={14} className="mr-1.5 text-[hsl(var(--gold))]" />Re-parse with premium AI</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT PROFILE PANEL (unchanged structure) */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-border rounded-xl p-5">
            <h3 className="font-head font-bold mb-4">Profile</h3>
            <div className="space-y-3">
              <Field label="Email" value={c.email} /><Field label="Phone" value={c.phone} />
              <Field label="Highest degree" value={`${c.highest_degree || ""} ${c.field_of_study ? `· ${c.field_of_study}` : ""}`.trim()} />
              <Field label="Years experience" value={c.years_experience ? `${c.years_experience} years` : ""} />
              <Field label="Licenses" value={listify(c.licenses).join(", ")} />
              <Field label="Certifications" value={listify(c.certifications).join(", ")} />
              <Field label="Languages" value={listify(c.languages).join(", ")} />
              <Field label="Populations served" value={listify(c.populations_served).join(", ")} />
              <Field label="Work settings" value={listify(c.work_settings).join(", ")} />
              <Field label="Leadership" value={c.leadership_experience} />
              <Field label="Supervisory" value={c.supervisory_experience} />
            </div>
          </div>
          {listify(c.skills).length > 0 && (
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="font-head font-bold mb-3">Skills</h3>
              <div className="flex flex-wrap gap-1.5">{listify(c.skills).map((s) => <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>)}</div>
            </div>
          )}
          {c.professional_summary && (
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="font-head font-bold mb-2">Résumé Summary</h3>
              <p className="text-sm text-foreground/75 leading-relaxed">{c.professional_summary}</p>
            </div>
          )}
          {listify(c.override_history).length > 0 && (
            <div className="bg-white border border-border rounded-xl p-5">
              <h3 className="font-head font-bold mb-3">Override audit trail</h3>
              <div className="space-y-3">{listify(c.override_history).map((o, i) => (
                <div key={i} className="text-xs border-l-2 border-[hsl(var(--teal))] pl-3">
                  <div><span className="text-muted-foreground">AI:</span> {o.original_classification || "—"} → <span className="font-medium">{o.revised_classification}</span></div>
                  <div className="text-muted-foreground">{o.recruiter} · {new Date(o.date).toLocaleDateString()}</div>
                  {o.note && <div className="italic mt-0.5">{o.note}</div>}
                </div>))}</div>
            </div>
          )}
        </div>

        {/* RIGHT ANALYSIS PANEL */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--teal))] font-semibold flex items-center gap-1.5"><Sparkles size={13} /> Flagship Analysis</div>
              <h2 className="font-head text-2xl font-extrabold tracking-tight">AI Candidate Match Analysis</h2>
            </div>
            {analysis && <Button variant="ghost" size="sm" onClick={() => fetchAnalysis(selectedPid, true)} data-testid="regen-analysis" title="Regenerate"><RefreshCw size={15} /></Button>}
          </div>

          {top.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-border rounded-xl"><p className="text-muted-foreground">No matches yet. Add active positions and re-run matching from the Upload page.</p></div>
          ) : aLoading ? (
            <div className="text-center py-20 bg-white border border-border rounded-xl"><Loader2 size={40} className="mx-auto text-[hsl(var(--teal))] animate-spin" /><p className="font-head font-bold text-lg mt-4">Generating AI analysis…</p><p className="text-muted-foreground text-sm mt-1">Reviewing the résumé against the position requirements.</p></div>
          ) : aError ? (
            <div className="text-center py-14 bg-white border border-border rounded-xl">
              <ShieldAlert size={34} className="mx-auto text-amber-500" />
              <p className="font-head font-bold mt-3">Analysis unavailable</p>
              <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">{aError}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => fetchAnalysis(selectedPid, true)}>Retry</Button>
            </div>
          ) : analysis && (
            <>
              {/* Score header */}
              <div className="bg-white border border-border rounded-xl p-6 fade-up">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Position Evaluated</div>
                <div className="font-head font-bold text-xl">{analysis.position_title}{analysis.client && <span className="text-muted-foreground font-normal text-base"> · {analysis.client}</span>}</div>
                <div className="flex items-center gap-8 mt-5">
                  <div className="flex items-center gap-3">
                    <ScoreRing score={analysis.overall_score} size={72} />
                    <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Overall Match</div><div className="font-head text-2xl font-extrabold" style={{ color: scoreColor(analysis.overall_score) }}>{analysis.overall_score}%</div></div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">AI Confidence</div>
                    <div className="flex items-center gap-2 mt-1"><span className="font-head text-2xl font-extrabold">{analysis.confidence}%</span><Badge variant="outline" className={confBadge(analysis.confidence_label)}>{analysis.confidence_label}</Badge></div>
                  </div>
                  <Badge variant="outline" className={`capitalize border ${CLASS_STYLES[analysis.classification] || ""}`}>{analysis.classification}</Badge>
                </div>
              </div>

              {/* Executive summary */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-2">Executive AI Summary</h3>
                <p className="text-sm text-foreground/85 leading-relaxed" data-testid="exec-summary">{analysis.executive_summary}</p>
              </div>

              {/* Why worth your time callout */}
              <div className="rounded-xl p-6 border border-blue-200 bg-blue-50/70" data-testid="why-callout">
                <h3 className="font-head font-bold text-lg text-blue-900 flex items-center gap-2"><ShieldCheck size={18} /> Why This Candidate Is Worth Your Time</h3>
                <p className="text-sm text-blue-900/85 leading-relaxed mt-2">{analysis.why_worth_time}</p>
              </div>

              {/* Match breakdown */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4">Match Breakdown</h3>
                <div className="space-y-3">
                  {analysis.breakdown.map((b) => (
                    <div key={b.key} className="flex items-center gap-4">
                      <div className="w-44 text-sm font-medium">{b.label}</div>
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(b.score / b.max) * 100}%`, background: b.status === "Meets Requirement" ? "hsl(var(--teal))" : "hsl(38 70% 52%)" }} /></div>
                      <div className="w-16 text-sm text-right tabular-nums">{b.score} / {b.max}</div>
                      <div className="w-40 text-xs">
                        {b.status === "Meets Requirement"
                          ? <span className="text-emerald-700 flex items-center gap-1"><CheckCircle2 size={13} /> Meets Requirement</span>
                          : <span className="text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> Needs Verification</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Evidence */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4">Evidence Supporting Match</h3>
                {listify(analysis.evidence).length === 0 ? <p className="text-sm text-muted-foreground">No direct evidence extracted.</p> : (
                  <div className="space-y-4">
                    {analysis.evidence.map((e, i) => (
                      <div key={i} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                        <div className="bg-secondary/60 rounded-lg p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Required</div><div className="text-sm font-medium">{e.requirement}</div></div>
                        <ArrowRight size={16} className="text-[hsl(var(--teal))]" />
                        <div className={`rounded-lg p-3 border ${e.found ? "bg-emerald-50/60 border-emerald-200" : "bg-amber-50/60 border-amber-200"}`}><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{e.found ? "Found on résumé" : "Not identified"}</div><div className="text-sm font-medium">{e.evidence || "Needs verification"}</div></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Needs verification + Risks */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border border-border rounded-xl p-6">
                  <h3 className="font-head font-bold text-lg mb-3">Needs Verification</h3>
                  {listify(analysis.needs_verification).length === 0 ? <p className="text-sm text-muted-foreground">Nothing outstanding.</p> : (
                    <ul className="space-y-1.5 text-sm">{analysis.needs_verification.map((n, i) => <li key={i} className="flex gap-2 text-amber-800"><AlertTriangle size={14} className="mt-0.5 shrink-0" />{n}</li>)}</ul>
                  )}
                  <p className="text-xs text-muted-foreground mt-3 italic">Not identified within the résumé — verify with the candidate.</p>
                </div>
                <div className="bg-white border border-border rounded-xl p-6">
                  <h3 className="font-head font-bold text-lg mb-3">Potential Risks</h3>
                  {listify(analysis.risks).length === 0 ? <p className="text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 size={15} /> No significant hiring risks identified.</p> : (
                    <ul className="space-y-1.5 text-sm">{analysis.risks.map((r, i) => <li key={i} className="flex gap-2 text-red-700"><ShieldAlert size={14} className="mt-0.5 shrink-0" />{r}</li>)}</ul>
                  )}
                </div>
              </div>

              {/* Transferable skills */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><Repeat size={17} className="text-blue-600" /> Transferable Skills</h3>
                {listify(analysis.transferable_skills).length === 0 ? <p className="text-sm text-muted-foreground">None identified.</p> : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {analysis.transferable_skills.map((t, i) => (
                      <div key={i} className="border border-border rounded-lg p-3"><div className="font-medium text-sm">{t.skill}</div><div className="text-xs text-muted-foreground mt-0.5">{t.value}</div></div>
                    ))}
                  </div>
                )}
              </div>

              {/* Candidate fit */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4">Candidate Fit</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {FIT_DIMS.map(([k, label]) => {
                    const v = analysis.candidate_fit?.[k] || "Unable to assess from résumé";
                    return (
                      <div key={k} className="border border-border rounded-lg p-4 text-center">
                        <div className="text-xs text-muted-foreground mb-2">{label}</div>
                        <Badge variant="outline" className={`border ${fitColor(v)}`}>{v}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Other positions */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4 flex items-center gap-2"><ArrowRightLeft size={17} className="text-[hsl(var(--teal))]" /> Other Positions This Candidate Matches</h3>
                <div className="grid md:grid-cols-2 gap-3">
                  {top.map((m) => (
                    <button key={m.position_id} data-testid={`other-pos-${m.position_id}`} onClick={() => setSelectedPid(m.position_id)}
                      className={`flex items-center justify-between gap-3 rounded-lg p-4 border text-left transition-colors ${m.position_id === selectedPid ? "border-[hsl(var(--teal))] bg-[hsl(var(--teal))]/5" : "border-border hover:border-[hsl(var(--teal))]/40"}`}>
                      <div className="min-w-0"><div className="font-medium truncate">{m.position_title}</div><Badge variant="outline" className="mt-1 text-[10px] capitalize">{m.classification}</Badge></div>
                      <span className="font-head font-extrabold text-lg" style={{ color: scoreColor(m.score || 0) }}>{m.score}%</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Recruiter actions */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4">Recruiter Actions</h3>
                <div className="flex flex-wrap gap-2">
                  {ACTIONS.map((a) => (
                    <Button key={a.label} variant="outline" size="sm" data-testid={`action-${a.status}-${a.label.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => changeStatus(a.status, a.toast)}>{a.label}</Button>
                  ))}
                  <Button variant="outline" size="sm" data-testid="action-assign" onClick={() => setAssignOpen(true)}>Assign Recruiter</Button>
                  <Button variant="outline" size="sm" data-testid="action-interview-guide" onClick={() => interviewRef.current?.scrollIntoView({ behavior: "smooth" })}>Generate Interview Guide</Button>
                  <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="action-override" onClick={() => { setOvClass(analysis.classification); setOvOpen(true); }}>Override AI classification</Button>
                </div>
              </div>

              {/* Interview prep */}
              <div ref={interviewRef} className="bg-white border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-head font-bold text-lg flex items-center gap-2"><MessageSquareText size={17} className="text-[hsl(var(--teal))]" /> AI Interview Preparation</h3>
                  <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-interview"><Printer size={14} className="mr-1.5" />Print / Export</Button>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {INTERVIEW_CATS.map(([k, label]) => {
                    const qs = listify(analysis.interview_prep?.[k]);
                    if (!qs.length) return null;
                    return (
                      <div key={k}>
                        <div className="font-semibold text-sm text-[hsl(var(--teal))] mb-1.5">{label}</div>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-foreground/80">{qs.map((q, i) => <li key={i}>{q}</li>)}</ol>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Explain the score */}
              <Collapsible className="bg-white border border-border rounded-xl">
                <CollapsibleTrigger className="w-full flex items-center justify-between p-5 hover:bg-secondary/30 transition-colors rounded-xl" data-testid="explain-score">
                  <span className="font-head font-bold">How TAG Recruit AI Calculated This Score</span>
                  <ChevronDown size={18} />
                </CollapsibleTrigger>
                <CollapsibleContent className="px-5 pb-5">
                  <p className="text-sm text-muted-foreground mb-3">Each category contributes a fixed maximum weight. The final score is the sum of points earned across all seven categories — never a black box.</p>
                  <div className="space-y-2">
                    {analysis.breakdown.map((b) => (
                      <div key={b.key} className="flex items-center justify-between text-sm border-b border-border py-1.5">
                        <span>{b.label}</span>
                        <span className="text-muted-foreground">weight {b.weight}% · earned <span className="font-semibold text-foreground">{b.score}</span></span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between font-head font-bold pt-2"><span>Overall Match Score</span><span>{analysis.overall_score}%</span></div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Export */}
              <div className="bg-white border border-border rounded-xl p-6">
                <h3 className="font-head font-bold text-lg mb-4">Export & Share</h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" data-testid="export-packet" onClick={() => window.open(`/packet/${id}/${selectedPid}`, "_blank")}><FileText size={14} className="mr-1.5" />Generate Candidate Packet (PDF)</Button>
                  <Button variant="outline" size="sm" data-testid="export-share" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/packet/${id}/${selectedPid}`); toast.success("Shareable packet link copied"); }}><Share2 size={14} className="mr-1.5" />Share with Client</Button>
                  <Button variant="outline" size="sm" data-testid="export-email" onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(`Candidate: ${c.name} — ${analysis.position_title}`)}&body=${encodeURIComponent(analysis.executive_summary)}`; }}><Mail size={14} className="mr-1.5" />Email Summary</Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Override dialog */}
      <Dialog open={ovOpen} onOpenChange={setOvOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Override AI classification</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Original AI decision is preserved for quality improvement.</p>
          <div className="space-y-3">
            <div className="text-sm">Position: <span className="font-medium">{analysis?.position_title}</span></div>
            <div className="text-sm">Original AI: <span className="font-medium">{analysis?.classification}</span></div>
            <Select value={ovClass} onValueChange={setOvClass}>
              <SelectTrigger data-testid="override-class-select"><SelectValue placeholder="Revised classification" /></SelectTrigger>
              <SelectContent>{CLASSIFICATIONS.map((cl) => <SelectItem key={cl} value={cl} className="capitalize">{cl}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Reason / note (optional)" value={ovNote} onChange={(e) => setOvNote(e.target.value)} data-testid="override-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOvOpen(false)}>Cancel</Button>
            <Button onClick={submitOverride} data-testid="save-override-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Save override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Assign Recruiter</DialogTitle></DialogHeader>
          <Input placeholder="Recruiter name or email" value={assignName} onChange={(e) => setAssignName(e.target.value)} data-testid="assign-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={submitAssign} data-testid="save-assign-btn" className="bg-[hsl(var(--teal))] hover:bg-[hsl(var(--teal))]/90">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
